import { Link } from "react-router-dom";

type Bucket = {
  label: string;
  range: string;
  amount: number;
  count: number;
  color: string;
  text: string;
};

const buckets: Bucket[] = [
  {
    label: "Current",
    range: "0–30 days",
    amount: 12480,
    count: 6,
    color: "bg-emerald-500",
    text: "text-emerald-700",
  },
  {
    label: "Due Soon",
    range: "31–60 days",
    amount: 7220,
    count: 3,
    color: "bg-amber-500",
    text: "text-amber-700",
  },
  {
    label: "Overdue",
    range: "61–90 days",
    amount: 3180,
    count: 1,
    color: "bg-orange-500",
    text: "text-orange-700",
  },
  {
    label: "Past Due",
    range: "90+ days",
    amount: 1300,
    count: 1,
    color: "bg-rose-500",
    text: "text-rose-700",
  },
];

const topOverdue = [
  {
    id: "INV-2204",
    customer: "Eleanor Whitfield",
    initials: "EW",
    color: "bg-fuchsia-500",
    amount: 1248,
    days: 14,
    projectId: "MV-2045",
  },
  {
    id: "INV-2198",
    customer: "Rebecca Lin",
    initials: "RL",
    color: "bg-cyan-600",
    amount: 1680,
    days: 22,
    projectId: "MV-2049",
  },
  {
    id: "INV-2186",
    customer: "Genesis Holdings LLC",
    initials: "GH",
    color: "bg-teal-600",
    amount: 3180,
    days: 64,
    projectId: "MV-2048",
  },
  {
    id: "INV-2171",
    customer: "Travis & Hannah Bell",
    initials: "TB",
    color: "bg-emerald-600",
    amount: 1300,
    days: 102,
    projectId: "MV-2044",
  },
];

export default function ArAging() {
  const total = buckets.reduce((s, b) => s + b.amount, 0);
  const overduePct = ((buckets[2].amount + buckets[3].amount) / total) * 100;

  return (
    <div className="bg-white border border-ink-100 rounded-lg p-6">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
        <div>
          <h3 className="font-display text-lg font-bold text-ink-900">
            Accounts Receivable
          </h3>
          <p className="text-xs text-ink-500 mt-0.5">
            Outstanding invoice aging buckets
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500">
            Total outstanding
          </div>
          <div className="font-display text-2xl font-bold text-ink-900 tabular-nums">
            ${total.toLocaleString()}
          </div>
          <div className="text-[10px] text-rose-600 font-bold">
            {overduePct.toFixed(0)}% past due
          </div>
        </div>
      </div>

      {/* Stacked bar */}
      <div className="w-full h-3 rounded-full overflow-hidden flex mt-5">
        {buckets.map((b) => (
          <div
            key={b.label}
            className={`${b.color} h-full`}
            style={{ width: `${(b.amount / total) * 100}%` }}
            title={`${b.label}: $${b.amount.toLocaleString()}`}
          ></div>
        ))}
      </div>

      {/* Bucket grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        {buckets.map((b) => (
          <div
            key={b.label}
            className="border border-ink-100 rounded-md p-3 hover:bg-cream-50 cursor-pointer transition-colors"
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span className={`w-2 h-2 rounded-full ${b.color}`}></span>
              <span className={`text-[10px] font-bold uppercase tracking-wider ${b.text}`}>
                {b.label}
              </span>
            </div>
            <div className="font-display text-xl font-bold text-ink-900 tabular-nums leading-tight">
              ${(b.amount / 1000).toFixed(1)}k
            </div>
            <div className="text-[10px] text-ink-500 mt-0.5 flex items-center justify-between">
              <span>{b.range}</span>
              <span className="font-bold tabular-nums">{b.count} inv</span>
            </div>
          </div>
        ))}
      </div>

      {/* Top overdue */}
      <div className="mt-5 pt-5 border-t border-ink-100">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-ink-700">
            Highest outstanding
          </h4>
          <button className="text-[11px] font-bold text-brand-700 hover:text-brand-800 cursor-pointer whitespace-nowrap">
            Send reminders →
          </button>
        </div>
        <ul className="space-y-2">
          {topOverdue.map((o) => (
            <li
              key={o.id}
              className="flex items-center gap-3 p-2 -mx-2 rounded-md hover:bg-cream-50 cursor-pointer"
            >
              <div
                className={`w-8 h-8 rounded-md ${o.color} text-white text-[10px] font-bold flex items-center justify-center shrink-0`}
              >
                {o.initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-ink-900 truncate">
                  {o.customer}
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-ink-500">
                  <span className="font-mono font-bold">{o.id}</span>
                  <span className="text-ink-300">·</span>
                  <Link
                    to={`/projects/${o.projectId}`}
                    className="font-mono text-brand-700 hover:underline cursor-pointer"
                  >
                    {o.projectId}
                  </Link>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-bold text-ink-900 tabular-nums">
                  ${o.amount.toLocaleString()}
                </div>
                <div
                  className={`text-[10px] font-bold ${
                    o.days > 60
                      ? "text-rose-600"
                      : o.days > 30
                      ? "text-orange-600"
                      : "text-amber-600"
                  }`}
                >
                  {o.days}d overdue
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}